import { InspectionFileNormError, ParserResult } from "./UploadFilesModels";

export class FileExtended {
  file: File;
  name: string;
  path: string;
  lowerCasePath: string;
  size: any;

  constructor(file: File, path: string) {
    this.file = file;
    this.path = path;
    this.name = file.name;
    this.size = file.size;
    this.lowerCasePath = path.toLowerCase();
  }

  get isAcceptedInspectionFileType() {
    return this.isXml || this.isXtf;
  }

  get isVideoFileType() {
    return this.file.type.startsWith("video/");
  }

  private get isXml() {
    return this.file.type === "text/xml";
  }

  private get isXtf() {
    return this.file.name.toLowerCase().endsWith(".xtf");
  }

  getFileFormData() {
    const form = new FormData();
    form.append("file", this.file);
    return form;
  }
}

export class FileUploadTracker {
  fileExtended: FileExtended;
  id?: number;
  name: string;
  path: string;
  progress: number;
  status:
    | "failed"
    | "ongoing"
    | "completed"
    | "excluded"
    | "missing"
    | "converting"
    | "converted"
    | "completedWithWarnings"
    | undefined;
  warning: string | undefined;
  error: string | undefined;
  parsingErrorsDetails: ParserResult[] = [];
  normError: InspectionFileNormError | null;
  size: number;
  canRetry: boolean;

  updateOverallProgress: VoidFunction | undefined;

  constructor(fileExtended: FileExtended, updateOverallProgress?: VoidFunction, mediaFileId?: number) {
    this.id = mediaFileId;
    this.fileExtended = fileExtended;
    this.name = fileExtended.file.name;
    this.path = fileExtended.path;
    this.progress = 0;
    this.updateOverallProgress = updateOverallProgress;
    this.normError = null;
    this.size = fileExtended.size;
    this.canRetry = false;
  }

  get isCompleted() {
    return this.status && this.status === "completed";
  }

  get isCompletedWithWarnings() {
    return this.status && this.status === "completedWithWarnings";
  }

  get isFailed() {
    return this.status && this.status === "failed";
  }

  get isExcluded() {
    return this.status && this.status === "excluded";
  }

  get isMissing() {
    return this.status && this.status === "missing";
  }

  get isFinished() {
    return this.status && this.status !== "ongoing";
  }

  ongoing() {
    this.status = "ongoing";
    if (this.updateOverallProgress) {
      this.updateOverallProgress();
    }
  }

  failed(err: any) {
    this.status = "failed";
    this.error = err;
    this.canRetry = true;
    if (this.updateOverallProgress) {
      this.updateOverallProgress();
    }
  }

  converted() {
    this.status = "converted";
    if (this.updateOverallProgress) {
      this.updateOverallProgress();
    }
  }

  converting() {
    this.status = "converting";
    if (this.updateOverallProgress) {
      this.updateOverallProgress();
    }
  }

  completed() {
    this.status = "completed";
    if (this.updateOverallProgress) {
      this.updateOverallProgress();
    }
  }

  completedWithWarnings(errors: ParserResult[], warning: string) {
    this.status = "completedWithWarnings";
    this.warning = warning;
    this.parsingErrorsDetails = errors;
    if (this.updateOverallProgress) {
      this.updateOverallProgress();
    }
  }

  excluded(warning: string) {
    this.status = "excluded";
    this.warning = warning;
    if (this.updateOverallProgress) {
      this.updateOverallProgress();
    }
  }

  notFound(warning: string) {
    this.status = "missing";
    this.warning = warning;
    this.canRetry = true;
  }

  updateProgress(progressEvent: ProgressEvent<EventTarget>) {
    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    this.progress = percentCompleted === 100 ? 99 : percentCompleted;

    if (this.updateOverallProgress) {
      this.updateOverallProgress();
    }
  }

  setNormError(normError: InspectionFileNormError) {
    this.normError = normError;
  }

  hasContent(): boolean {
    return this.fileExtended.file.size > 0;
  }

  updateFileExtended(fileExtended: FileExtended) {
    this.fileExtended = fileExtended;
    this.path = fileExtended.path;
    this.size = fileExtended.size;
  }

  getFileFormData() {
    return this.fileExtended.getFileFormData();
  }
}

export class FileUploadTrackerWrapper {
  wrapper: FileUploadTracker;

  constructor(fileExtended: FileExtended, updateOverallProgress?: VoidFunction, mediaFileId?: number) {
    this.wrapper = new FileUploadTracker(fileExtended, updateOverallProgress, mediaFileId);
  }

  get name() { return this.wrapper.name; }
  get status() { return this.wrapper.status; }
}
